import * as React from 'react';

import type { EnterpriseBadge_CriteriaItemInput } from '__generated__/graphql-types';

import { DEFAULT_BADGE_LOGO_HEIGHT } from 'bundles/enterprise-admin-learning-path/constants';

type Skill = {
  skillId: string;
  skillName: string;
};

export type LearningPathCreationContextType = {
  learningPathName: string;
  setLearningPathName: React.Dispatch<React.SetStateAction<string>>;
  badgeName: string;
  setBadgeName: React.Dispatch<React.SetStateAction<string>>;
  badgeLogoHeight: number;
  setBadgeLogoHeight: React.Dispatch<React.SetStateAction<number>>;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  learningObjectives: string[] | null;
  setLearningObjectives: React.Dispatch<React.SetStateAction<string[] | null>>;
  criteria: EnterpriseBadge_CriteriaItemInput[];
  setCriteria: React.Dispatch<React.SetStateAction<EnterpriseBadge_CriteriaItemInput[]>>;
  skills: Skill[];
  setSkills: React.Dispatch<React.SetStateAction<Skill[]>>;
  uploadedBrandingImage: string;
  setUploadedBrandingImage: React.Dispatch<React.SetStateAction<string>>;
  brandingImage: string;
  setBrandingImage: React.Dispatch<React.SetStateAction<string>>;
  imageUrl: string;
  setImageUrl: React.Dispatch<React.SetStateAction<string>>;
  resetContext: () => void;
};

export const LearningPathCreationContext = React.createContext<LearningPathCreationContextType | undefined>(undefined);

type LearningPathCreationProviderProps = {
  initialLearningObjectives?: string[];
  children: React.ReactNode;
};

export const LearningPathCreationProvider: React.FC<LearningPathCreationProviderProps> = ({
  children,
  initialLearningObjectives = null,
}) => {
  const [learningPathName, setLearningPathName] = React.useState<string>('');
  const [badgeName, setBadgeName] = React.useState<string>('');
  const [badgeLogoHeight, setBadgeLogoHeight] = React.useState<number>(DEFAULT_BADGE_LOGO_HEIGHT);
  const [description, setDescription] = React.useState<string>('');
  const [learningObjectives, setLearningObjectives] = React.useState<string[] | null>(initialLearningObjectives);
  const [criteria, setCriteria] = React.useState<EnterpriseBadge_CriteriaItemInput[]>([]);
  const [skills, setSkills] = React.useState<Skill[]>([]);
  const [uploadedBrandingImage, setUploadedBrandingImage] = React.useState<string>('');
  const [brandingImage, setBrandingImage] = React.useState<string>('');
  const [imageUrl, setImageUrl] = React.useState<string>('');

  const resetContext = React.useCallback<() => $TSFixMe>(() => {
    setLearningPathName('');
    setBadgeName('');
    setDescription('');
    setLearningObjectives([]);
    setCriteria([]);
    setSkills([]);
    setUploadedBrandingImage('');
    setBrandingImage('');
    setImageUrl('');
  }, []);

  return (
    <LearningPathCreationContext.Provider
      value={{
        learningPathName,
        setLearningPathName,
        badgeName,
        setBadgeName,
        badgeLogoHeight,
        setBadgeLogoHeight,
        description,
        setDescription,
        learningObjectives,
        setLearningObjectives,
        criteria,
        setCriteria,
        skills,
        setSkills,
        uploadedBrandingImage,
        setUploadedBrandingImage,
        brandingImage,
        setBrandingImage,
        imageUrl,
        setImageUrl,
        resetContext,
      }}
    >
      {children}
    </LearningPathCreationContext.Provider>
  );
};

export const useLearningPathCreation = () => {
  const context = React.useContext(LearningPathCreationContext);
  if (context === undefined) {
    throw new Error('useLearningPathCreation must be used within a LearningPathCreationContext');
  }
  return context;
};
